import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import { formatInterval } from 'lib/formatInterval';
import TZDate from 'lib/TZDate';
import { tzmoment } from 'lib/tzmoment';

import { MilestoneTypes as MSType } from 'data/models/MilestoneTypes';
import { ScheduleDateEntity as DateEntity } from 'data/models/ScheduleDateEntity';
import { ScheduleDateType as SDType } from 'data/models/ScheduleDateType';

import { MetroMapFramework, MetroMapMilestone } from './model';

export interface MetroMapFrameworkViewProps<TMilestone> {
    model : MetroMapFramework<TMilestone>;
    ctrl : MilestoneViewController<TMilestone>;
}

/**
 * Basic scaffolding for any variation on our "metro map" UI concept. Displays UI for all
 * milestones in a PortCall in a single linked timeline. This component is concerned w/ the
 * overall structure of the timeline and the presentation of the line that runs down the
 * timeline's center but is generic over the content renderered alongside the timeline (the
 * actual "stops" on the metro map).
 */
export class MetroMapFrameworkView<TMilestone extends MetroMapMilestone = MetroMapMilestone> extends SuperCriticalComponent<MetroMapFrameworkViewProps<TMilestone>> {
    srender({ model, ctrl } : MetroMapFrameworkViewProps<TMilestone>) {
        return (
            <div className="portcall-schedule__milestones">
                {model.Milestones().map(m =>
                    <MilestoneView milestone={m} ctrl={ctrl} key={MilestoneKey(m)} />
                )}
            </div>
        );
    }
}

let nextMilestoneKey = 0;
const MILESTONE_KEYS = new WeakMap<DateEntity, number>();
function MilestoneKey(m : MetroMapMilestone) {
    // To handle both persisted and non-persisted date entities, use a keying strategy based on DateEntity
    // object identity.
    let id = MILESTONE_KEYS.get(m.Date);
    if (id === undefined) {
        id = nextMilestoneKey++;
        MILESTONE_KEYS.set(m.Date, id);
    }
    return id;
}

interface MilestoneViewProps<TMilestone> {
    /** The milestone model for which to render this milestone view. */
    milestone : TMilestone;
    /** A controller that specifies the contents and other attributes of this milestone view. */
    ctrl : MilestoneViewController<TMilestone>;
    /**
     * Optionally instructs the milestone view to render itself in standalone mode, meaning the milestone is
     * being rendered outside of the context of a full metro map visualization. The default is to render in
     * linked mode, i.e. as part of a metro map visualization for an entire portcall.
     */
    standalone? : boolean;
}

export type MilestoneElementProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface MilestoneViewController<TMilestone> {
    milestoneContent(milestone : TMilestone) : React.ReactElement<any> | null;
    milestoneElementProps?(milestone : TMilestone) : MilestoneElementProps;
    milestoneHasClassname?(milestone : TMilestone, classname : OverridableMilestonElementClassname) : boolean | undefined;
}

/**
 * Generic UI component that renders a single milestone (aka "stop") on a metro map timeline.
 * Renders the line UI spanning from the passed-in milestone up to (and possibly including) the
 * next milestone. Calls the provided @see MilestoneController to determine which content to
 * display alongside the timeline for this milestone.
 */
export class MilestoneView<TMilestone extends MetroMapMilestone = MetroMapMilestone> extends SuperCriticalComponent<MilestoneViewProps<TMilestone>> {
    srender({ milestone: m, ctrl, standalone } : MilestoneViewProps<TMilestone>) {
        return (
            <div {...milestoneElementProps(m, ctrl, standalone)}>
                {MilestoneLine(m)}
                {ctrl.milestoneContent(m)}
            </div>
        );
    }
}

export type OverridableMilestonElementClassname =
    'portcall-milestone--has-actual';

function milestoneElementProps<TMilestone extends MetroMapMilestone>(
    m : TMilestone,
    ctrl : MilestoneViewController<TMilestone>,
    standalone : boolean | undefined
) : MilestoneElementProps {
    const
        provided = ctrl.milestoneElementProps?.(m),
        hasActual = ctrl.milestoneHasClassname?.(m, 'portcall-milestone--has-actual') ?? m.Date.Actual() !== null;
    return {
        ...provided,
        className: cx(
            provided?.className,
            'portcall-milestone',
            `portcall-milestone--${standalone || !m.Date.NextMilestoneDate() ? 'has-line-cap' : 'has-no-line-cap'}`,
            `portcall-milestone--${m.Date.IsMovementStartDate() ? 'movement' : 'inport'}`,
            `portcall-milestone--${hasActual ? 'has-actual' : 'has-no-actual'}`
        )
    };
}

const
    MilestoneLine = (m : MetroMapMilestone) =>
        [
            m.Date.IsMovementStartDate() ? MilestoneLineMovement : MilestoneLineInPort,
            /* since the next movement icon is usualy displayed by the next movement, we need
            to add a "cap" that shows it for the last movement */
            m.Date.IsMovementStartDate() ? MilestoneLineMovementCap : MilestoneLineInPortCap
        ],
    MilestoneLineMovement = (
        <svg className="portcall-milestone__movement-line" key="line">
            <rect x="9" y="6" width="2" height="100%" />
            <circle cx="10" cy="8" r="5" />
            <circle cx="10" cy="8" r="2.5" />
        </svg>
    ),
    MilestoneLineMovementCap = (
        <svg className="portcall-milestone__movement-line-cap" key="line-cap">
            <rect x="5" y="4" width="10" height="2" />
        </svg>
    ),
    MilestoneLineInPort = (
        <svg className="portcall-milestone__inport-line" key="line">
            <rect x="6" y="6" width="8" height="100%" />
            <rect x="5" y="6" width="10" height="2" />
        </svg>
    ),
    MilestoneLineInPortCap = (
        <svg className="portcall-milestone__inport-line-cap" key="line-cap">
            <circle cx="10" cy="5" r="5" />
            <circle cx="10" cy="5" r="2.5" />
        </svg>
    );

export const MilestoneHeaderRow = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    <div className="portcall-milestone-row portcall-milestone__header-row">
        <MilestoneHeaderRowLabel milestone={m} />
        <MilestoneHeaderRowBody milestone={m} />
    </div>
);

export const MilestoneHeaderRowLabel = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    <div className="portcall-milestone-row-label">
        {MilestoneHeaderDateText(m)}
    </div>
);

export function MilestoneHeaderDateText(m : MetroMapMilestone | TZDate) {
    const d = m instanceof TZDate ? m : m.Date.Effective();
    return tzmoment(d).format('DD MMM - HH:mm');
}

export const MilestoneHeaderRowBody = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    <div className="portcall-milestone-row-body">
        <span>{DateTypeParticiple(m)}</span>
        {' '}
        <span className="portcall-milestone__header-berth-name">
            {m.Date.ItineraryPoint()?.Location()?.Name()}
        </span>
    </div>
);

export function DateTypeParticiple(m : MetroMapMilestone) {
    return (
        m.Date.Type() === SDType.IPArrival ?
            m.Date.Actual() === null ? 'Arriving' : 'Arrived' :
        m.Date.Type() === SDType.IPDeparture ?
            m.Date.Actual() === null ? 'Departing' : 'Departed' :
        ''
    );
}

export const MilestoneScheduleDetailsRow = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    <div className="portcall-milestone-row portcall-milestone__schedule-details-row">
        <MilestoneScheduleDetailsRowLabel milestone={m} />
        <MilestoneScheduleDetailsRowBody milestone={m} />
    </div>
);

// Don't currently need the milestone prop, but including as future-proofing.
export const MilestoneScheduleDetailsRowLabel = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    <div className="portcall-milestone-row-label">
        {m.Date.IsMovementStartDate() ? 'Movement' : ''}
    </div>
);

export const MilestoneScheduleDetailsRowBody = SuperCritical(({ milestone: m } : { milestone : MetroMapMilestone }) =>
    m.Date.IsMovementStartDate() ?
        <div className="portcall-milestone-row-body">
            <span>
                {MovementTypeParticiple(m)}
                {' '}
                {m.Date.Actual() === null && MovementToLocationText(m)}
                {' '}
                &mdash;
            </span>
            {' '}
            {m.Date.Actual() === null ?
                <span className="pc-text-smalllabel">{MovementTransitDurationText(m)}</span> :
                <span className="portcall-milestone__log-actual-attribution">
                    {LogActualAttributionText(m)}
                </span>
            }
        </div> :
        <div className="portcall-milestone-row-body">
            <span className="portcall-milestone__log-actual-attribution">
                {LogActualAttributionText(m)}
            </span>
        </div>
);

// TODO: Update me for the new IN/OUT/SHIFT/AREA movement categorization scheme used on the
// homepage's Traffic Schedule tab?
export function MovementTypeParticiple(m : MetroMapMilestone) {
    return (
        m.Date.MilestoneTypeId() === MSType.FirstArrival   ? 'Inbound'  :
        m.Date.MilestoneTypeId() === MSType.ShiftDeparture ? 'Shifting' :
        m.Date.MilestoneTypeId() === MSType.FinalDeparture ? 'Outbound' :
        ''
    );
}

export function MovementToLocationText(m : MetroMapMilestone) {
    return (
        m.Date.MilestoneTypeId() === MSType.FirstArrival ||
        m.Date.MilestoneTypeId() === MSType.ShiftDeparture ?
            `to ${m.NextDate()?.ItineraryPoint()?.Location()?.Name() ?? '(loading...)'}` :
        ''
    );
}

export function MovementTransitDurationText(m : MetroMapMilestone) : string;
export function MovementTransitDurationText(from : TZDate, to? : TZDate | null) : string;
export function MovementTransitDurationText(from : MetroMapMilestone | TZDate, to? : TZDate | null) {
    if (!(from instanceof TZDate)) {
        to = from.NextDate()?.Effective();
        from = from.Date.Effective();
    }
    if (to === undefined || to === null) return '';
    return `${formatInterval(from, to)} transit`;
}

export function LogActualAttributionText(m : MetroMapMilestone) {
    const
        loggedTime = m.Date.Actual(),
        ev = m.ActualLoggedEvent();
    if (loggedTime === null || ev === null) return null;
    const
        lineTimeText =
            m.Date.IsBerthArrivalDate() ?
                m.Date.Type() === SDType.IPArrival   ? 'First line' :
                m.Date.Type() === SDType.IPDeparture ? 'Last line'  :
                '' :
            '',
        dateFormat =
            ev.Date().getFullYear() === loggedTime.getFullYear() ?
                ev.Date().getMonth() === loggedTime.getMonth() &&
                ev.Date().getDate() === loggedTime.getDate() ?
                    'HH:mm' :
                    'HH:mm [on] DD MMM' :
                'HH:mm [on] DD MMM YYYY';
    return `${lineTimeText ? `${lineTimeText} logged` : 'Logged'} by ${ev.ActorAbbreviatedName()} at ${tzmoment(ev.Date()).format(dateFormat)}`;
}

export function BerthStayDurationText(m : MetroMapMilestone) : string;
export function BerthStayDurationText(from : TZDate, to? : TZDate | null) : string;
export function BerthStayDurationText(from : MetroMapMilestone | TZDate, to? : TZDate | null) {
    if (!(from instanceof TZDate)) {
        to = from.NextDate()?.Effective();
        from = from.Date.Effective();
    }
    if (to === undefined || to === null) return '';

    // From the spec: "Reservation Duration in nights. For a portcall with no overnight stay, show hours. 
    // Examples: 1 night stay with 12 hour duration = “Overnight”. Multiple nights = “3 nights”, 
    // No overnight stay with 12 hour reservation = “12 hours”. 
    const
        fromM = tzmoment(from),
        toM = tzmoment(to),
        daysDiff = toM.dayOfYear() - fromM.dayOfYear(),
        hoursDiff = toM.diff(fromM, 'hours');

    return daysDiff > 1 ? `${daysDiff} nights` :
        daysDiff === 1 ? 'Overnight' :
        hoursDiff === 1 ? '1 hour' :
        `${hoursDiff} hours`;
}



// WEBPACK FOOTER //
// ./apps/portcall/metromapframework/view.tsx